import { useEffect, useMemo, useState } from "react";
import { Loader2, Mail, MessageSquare, Smartphone } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  SMS_TRANSACTIONAL_CONSENT_REQUIRED_MESSAGE,
  SmsConsentConfirmation,
} from "@/components/shared/SmsConsentConfirmation";
import type { ApprovalDeliveryMethod } from "@/lib/tickets";
import type { PaymentFollowupHistoryItem } from "@/lib/billing-exports";

type PaymentFollowUpTarget = {
  projectName: string;
  clientName?: string | null;
  clientEmail?: string | null;
  clientPhone?: string | null;
};

function normalizePhoneForDelivery(value?: string | null) {
  const raw = value?.trim();
  if (!raw) return null;

  if (raw.startsWith("+")) {
    const normalized = `+${raw.slice(1).replace(/\D/g, "")}`;
    return /^\+\d{10,15}$/.test(normalized) ? normalized : null;
  }

  const digits = raw.replace(/\D/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return null;
}

function formatDeliveryMethodLabel(item: PaymentFollowupHistoryItem) {
  if (item.deliveryMethod === "EMAIL_AND_SMS") return "Email and SMS";
  if (item.deliveryMethod === "EMAIL") return "Email";
  if (item.deliveryMethod === "SMS") return "SMS";
  if (item.sentChannels?.length) {
    return item.sentChannels
      .map((channel) => (channel.toLowerCase() === "sms" ? "SMS" : "Email"))
      .join(" and ");
  }
  return "Follow-up";
}

function formatFollowupStatus(status?: string | null) {
  if (!status) return null;
  return status
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function SendPaymentFollowUpDialog({
  open,
  onOpenChange,
  target,
  loading,
  smsAllowedByPlan,
  history = [],
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: PaymentFollowUpTarget | null;
  loading: boolean;
  smsAllowedByPlan: boolean;
  history?: PaymentFollowupHistoryItem[];
  onConfirm: (
    deliveryMethod: ApprovalDeliveryMethod,
    smsConsentConfirmed?: boolean,
  ) => Promise<void> | void;
}) {
  const smsEnabledByEnv = import.meta.env.VITE_SMS_APPROVALS_ENABLED === "true";
  const normalizedPhone = useMemo(
    () => normalizePhoneForDelivery(target?.clientPhone),
    [target?.clientPhone],
  );
  const hasEmail = Boolean(target?.clientEmail?.trim());
  const hasPhone = Boolean(normalizedPhone);
  const canUseSms = smsEnabledByEnv && smsAllowedByPlan && hasPhone;

  const availableMethods = useMemo<ApprovalDeliveryMethod[]>(() => {
    const methods: ApprovalDeliveryMethod[] = [];
    if (hasEmail) methods.push("EMAIL");
    if (canUseSms) methods.push("SMS");
    if (hasEmail && canUseSms) methods.push("EMAIL_AND_SMS");
    return methods;
  }, [canUseSms, hasEmail]);

  const [deliveryMethod, setDeliveryMethod] = useState<ApprovalDeliveryMethod>("EMAIL");
  const [smsConsentConfirmed, setSmsConsentConfirmed] = useState(false);
  const [smsConsentError, setSmsConsentError] = useState("");
  const deliveryUsesSms = deliveryMethod === "SMS" || deliveryMethod === "EMAIL_AND_SMS";

  useEffect(() => {
    if (!open) return;
    if (availableMethods.includes("EMAIL")) {
      setDeliveryMethod("EMAIL");
      return;
    }
    if (availableMethods.includes("SMS")) {
      setDeliveryMethod("SMS");
      return;
    }
    if (availableMethods.includes("EMAIL_AND_SMS")) {
      setDeliveryMethod("EMAIL_AND_SMS");
    }
  }, [availableMethods, open]);

  useEffect(() => {
    if (!open || !deliveryUsesSms) {
      setSmsConsentConfirmed(false);
      setSmsConsentError("");
    }
  }, [deliveryUsesSms, open]);

  const smsReason = !smsEnabledByEnv
    ? "SMS is disabled in this environment."
    : !smsAllowedByPlan
      ? "SMS follow-up is available on Pro and Team."
      : !hasPhone
        ? "A valid client phone number is required for SMS."
        : null;

  const emailReason = !hasEmail ? "A client email address is required for email follow-up." : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>Send Payment Follow-up</DialogTitle>
          <DialogDescription>
            Choose how to contact {target?.clientName?.trim() || "the client"} about the outstanding balance for{" "}
            <span className="font-medium text-foreground">{target?.projectName ?? "this project"}</span>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md border border-border bg-muted/20 p-3 text-sm">
            <div className="font-medium text-card-foreground">{target?.clientName || "Client"}</div>
            <div className="mt-1 space-y-1 text-muted-foreground">
              <div className="flex items-center gap-2">
                <Mail className="h-3.5 w-3.5" />
                <span>{target?.clientEmail?.trim() || "No email on file"}</span>
              </div>
              <div className="flex items-center gap-2">
                <Smartphone className="h-3.5 w-3.5" />
                <span>{normalizedPhone || target?.clientPhone?.trim() || "No phone on file"}</span>
              </div>
            </div>
          </div>

          <div className="rounded-md border border-border bg-background p-3">
            <div className="text-sm font-medium text-card-foreground">Follow-up history</div>
            {history.length ? (
              <div className="mt-2 space-y-1.5 text-sm text-muted-foreground">
                {history.map((item) => {
                  const status = formatFollowupStatus(item.status);
                  return (
                    <div key={item.id} className="leading-5">
                      {new Date(item.sentAt).toLocaleString(undefined, {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                      })}{" "}
                      · {formatDeliveryMethodLabel(item)}
                      {status ? ` · ${status}` : ""}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="mt-2 text-sm text-muted-foreground">
                No payment follow-ups have been sent yet.
              </p>
            )}
          </div>

          {availableMethods.length ? (
            <div className="space-y-2">
              <Label>Send via</Label>
              <RadioGroup
                value={deliveryMethod}
                onValueChange={(value) => setDeliveryMethod(value as ApprovalDeliveryMethod)}
                className="gap-3"
              >
                {hasEmail ? (
                  <label className="flex cursor-pointer items-start gap-3 rounded-md border border-border px-3 py-3">
                    <RadioGroupItem value="EMAIL" id="payment-followup-email" className="mt-0.5" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-card-foreground">Email</div>
                      <div className="text-xs text-muted-foreground">{target?.clientEmail}</div>
                    </div>
                  </label>
                ) : null}
                {canUseSms ? (
                  <label className="flex cursor-pointer items-start gap-3 rounded-md border border-border px-3 py-3">
                    <RadioGroupItem value="SMS" id="payment-followup-sms" className="mt-0.5" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-card-foreground">SMS</div>
                      <div className="text-xs text-muted-foreground">{normalizedPhone}</div>
                    </div>
                  </label>
                ) : null}
                {hasEmail && canUseSms ? (
                  <label className="flex cursor-pointer items-start gap-3 rounded-md border border-border px-3 py-3">
                    <RadioGroupItem
                      value="EMAIL_AND_SMS"
                      id="payment-followup-email-and-sms"
                      className="mt-0.5"
                    />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 text-sm font-medium text-card-foreground">
                        <MessageSquare className="h-3.5 w-3.5" />
                        Email and SMS
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Send the follow-up by both channels.
                      </div>
                    </div>
                  </label>
                ) : null}
              </RadioGroup>
            </div>
          ) : (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
              No delivery channel is available for this client. Add an email address or a valid phone number first.
            </div>
          )}

          {emailReason ? <p className="text-xs text-muted-foreground">{emailReason}</p> : null}
          {smsReason ? <p className="text-xs text-muted-foreground">{smsReason}</p> : null}
          {deliveryUsesSms ? (
            <SmsConsentConfirmation
              checked={smsConsentConfirmed}
              onCheckedChange={(checked) => {
                setSmsConsentConfirmed(checked);
                if (checked) setSmsConsentError("");
              }}
              error={smsConsentError}
            />
          ) : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => {
              if (deliveryUsesSms && !smsConsentConfirmed) {
                setSmsConsentError(SMS_TRANSACTIONAL_CONSENT_REQUIRED_MESSAGE);
                return;
              }
              void onConfirm(deliveryMethod, deliveryUsesSms ? smsConsentConfirmed : undefined);
            }}
            disabled={
              loading ||
              availableMethods.length === 0 ||
              (deliveryUsesSms && !smsConsentConfirmed)
            }
            className="gap-2"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {loading ? "Sending..." : "Send follow-up"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
